<template>
  <nav v-show="shouldShowCategoriesBar" class="bg-transparent">
    <div class="max-w-10xl mx-auto bg-white/90 backdrop-blur border-b-2 border-b-brand-800">
      <div class="flex h-14">
        <div class="flex shrink-0 z-10 cursor-pointer" @click="goToRoot">
          <img
            v-if="$store.getters.merchant"
            class="w-14"
            :src="$store.getters.merchant?.attributes.avatar_url"
            :alt="$store.getters.imageAlt($store.getters.merchant?.attributes.user_name)"
          />
        </div>

        <div
          ref="categoryBar"
          class="flex-1 min-w-0 flex flex-nowrap overflow-x-auto overflow-y-hidden whitespace-pre pl-2"
        >
          <button
            v-for="cat in categories"
            :id="`category-menu-item-${cat.id}`"
            :key="cat.id"
            class="text-lg sm:text-xl h-full inline-flex flex-col items-center justify-center text-gray-brand-800 px-2 cursor-pointer after:content-[attr(data-text)] after:font-bold after:h-0 after:invisible tracking-tighter"
            :data-text="cat.name"
            :class="{
              'font-bold': activeCategory?.id === cat.id
            }"
            role="listitem"
            @click="handleCategoryClick(cat)"
          >
            {{ cat.name }}
          </button>
        </div>

        <!-- This bar replaces the nav once the page scrolls, and the nav is where the cart pill
             lives - so without this the cart summary disappears the moment a customer starts
             browsing, which is exactly when they want it. The pill hides itself below `md`; on a
             phone the sticky basket bar is the cart. -->
        <MerchantCartPill
          v-if="cartModals && cartQuantity > 0"
          :quantity="cartQuantity"
          :total="cartTotal"
          :show-price="showPrice"
          class="self-center flex-none mr-3"
          @click="$emit('showCart', true)"
        />

        <div class="flex shrink-0">
          <MerchantNavMenu button-id="categories-bar-menu-button" :empty-middle="true" />
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import MerchantNavMenu from '@/components/Merchant/MerchantNavMenu'
import MerchantCartPill from '@/components/Merchant/MerchantCartPill.vue'
import { cartPillSummary } from '@/utils/cartPillSummary'

export default {
  components: { MerchantNavMenu, MerchantCartPill },

  emits: ['showCart'],

  props: {
    // Flat v2 store-snapshot categories ({ id, name, type, products }) — v2
    // sources categories from TanStack Query, not Vuex, so the V1 bar (which
    // reads store.getters.categories) can't be reused directly.
    categories: {
      type: Array,
      default: () => []
    },
    // Same arrangement the nav uses: V2's cart is in TanStack Query rather than Vuex, so the view
    // passes these down and cartPillSummary falls through to the getters when they are absent.
    v2CartQuantity: { type: Number, default: null },
    v2CartTotal: { type: String, default: null },
    v2ShowPrices: { type: Boolean, default: null }
  },

  computed: {
    cartModals() {
      return this.$store.getters.hasCartModals
    },

    cartPill() {
      return cartPillSummary(this.$store, {
        quantity: this.v2CartQuantity,
        total: this.v2CartTotal,
        showPrices: this.v2ShowPrices
      })
    },

    cartQuantity() {
      return this.cartPill.quantity
    },

    cartTotal() {
      return this.cartPill.total
    },

    showPrice() {
      return this.cartPill.showPrice
    }
  },
  setup(props) {
    const shouldShowCategoriesBar = ref(false)
    const activeCategory = ref(null)
    const categoryBar = ref(null)
    let prevCategory = null

    const listener = () => {
      shouldShowCategoriesBar.value = window.scrollY > 30 && props.categories.length > 1

      // reset scroll when returning to top
      if (window.scrollY <= 30) {
        categoryBar.value?.scrollTo({ left: 0 })
      }

      activeCategory.value = props.categories
        .map((x) => [
          x,
          document.getElementById('category-' + x?.id)?.getBoundingClientRect().bottom - window.screen.height + 300
        ])
        .filter((x) => x[1] < 0)
        .pop()?.[0]

      const cat = document.getElementById('category-menu-item-' + activeCategory.value?.id)
      if (activeCategory.value !== prevCategory && cat) {
        const catCenter = cat.offsetLeft + cat.offsetWidth / 2
        const barCenter = categoryBar.value.offsetLeft + categoryBar.value.offsetWidth / 2
        categoryBar.value.scrollTo({
          left: catCenter - barCenter,
          behavior: 'smooth'
        })
        prevCategory = activeCategory.value
      }
    }

    onMounted(() => {
      shouldShowCategoriesBar.value = window.scrollY > 30 && props.categories.length > 1
      document.addEventListener('scroll', listener)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('scroll', listener)
    })

    function handleCategoryClick(category) {
      // V2Category only renders its `category-${id}` anchor when not in
      // upsell/cart mode, so the target can be absent — guard against null.
      const el = document.getElementById(`category-${category.id}`)
      if (!el) return
      const offset = el.getBoundingClientRect().top - 100
      window.scrollBy({ top: offset, behavior: 'smooth' })
    }

    return {
      shouldShowCategoriesBar,
      handleCategoryClick,
      activeCategory,
      categoryBar
    }
  },
  methods: {
    goToRoot() {
      let avatarClickUrl = this.$store.getters.merchant?.attributes?.avatar_click_url
      if (avatarClickUrl) {
        if (!avatarClickUrl.startsWith('http://') && !avatarClickUrl.startsWith('https://')) {
          avatarClickUrl = 'https://' + avatarClickUrl
        }
        window.open(avatarClickUrl, '_blank')
      } else {
        this.$router.push({ name: 'Home' })
      }
    }
  }
}
</script>
